import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logService } from '../lib/logService';
import { getCookie, setCookie, deleteCookie } from '../lib/cookies';


// =====================================================
// HOOK usePartialSave — Auto-Save + Cookie Session
// Story 5.2 (EPIC-PARTIAL-SUBMIT)
// =====================================================

interface UsePartialSaveOptions {
  formId: string;
  enabled: boolean;
  cookieExpiryDays?: number;
}

interface UsePartialSaveReturn {
  sessionToken: string | null;
  isSaving: boolean;
  lastSavedAt: Date | null;
  responseId: string | null;
  savePartial: (data: Record<string, any>, lastFieldId: string) => Promise<void>;
  flush: () => Promise<void>;
  submitFinal: (data: Record<string, any>, scores?: Record<string, any> | null) => Promise<{ error: string | null }>;
  hasExistingSession: boolean;
  existingSessionData: Record<string, any> | null;
  existingSessionUpdatedAt: string | null;
  clearSession: () => void;
}

const COOKIE_PREFIX = 'vf_session_';
const DEBOUNCE_MS = 2000;
const RETRY_DELAY_MS = 3000;

// Check if data has at least one non-empty value
// Layout fields (NON_INPUT_FIELD_TYPES) should never appear in data —
// the consumer (FormTypeform) only tracks input field values.
function hasInputData(data: Record<string, any>): boolean {
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (val !== undefined && val !== null && val !== '') return true;
  }
  return false;
}

function cookieName(formId: string): string {
  return `${COOKIE_PREFIX}${formId}`;
}

function parseCookie(raw: string | null): { sessionToken: string; responseId: string } | null {
  if (!raw) return null;
  const [sessionToken, responseId] = raw.split(':');
  if (!sessionToken || !responseId) return null;
  return { sessionToken, responseId };
}

// Noop return when partial save is disabled
const NOOP_RETURN: UsePartialSaveReturn = {
  sessionToken: null,
  isSaving: false,
  lastSavedAt: null,
  responseId: null,
  savePartial: async () => {},
  flush: async () => {},
  submitFinal: async () => ({ error: 'Partial save not enabled' }),
  hasExistingSession: false,
  existingSessionData: null,
  existingSessionUpdatedAt: null,
  clearSession: () => {},
};

export function usePartialSave(options: UsePartialSaveOptions): UsePartialSaveReturn {
  const { formId, enabled, cookieExpiryDays = 30 } = options;

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [existingSessionData, setExistingSessionData] = useState<Record<string, any> | null>(null);
  const [existingSessionUpdatedAt, setExistingSessionUpdatedAt] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestDataRef = useRef<Record<string, any> | null>(null);
  const latestFieldRef = useRef<string | null>(null);
  const insertingRef = useRef(false);

  // Check for existing cookie on mount
  useEffect(() => {
    if (!enabled) return;

    const existing = parseCookie(getCookie(cookieName(formId)));
    if (!existing) return;

    setSessionToken(existing.sessionToken);
    setResponseId(existing.responseId);

    // Fetch existing partial data from DB
    const restore = async () => {
      try {
        const { data, error } = await supabase.rpc('get_partial_response', {
          p_form_id: formId,
          p_session_token: existing.sessionToken,
        });

        if (error) {
          logService.error('partialSave.restore', error.message, { supabase_code: error.code, form_id: formId });
          // Invalid session — clean up
          deleteCookie(cookieName(formId));
          setSessionToken(null);
          setResponseId(null);
          return;
        }

        const row = Array.isArray(data) ? data[0] : data;
        if (row && row.data) {
          setHasExistingSession(true);
          setExistingSessionData(row.data);
          setExistingSessionUpdatedAt(row.updated_at || null);
        } else {
          // No in_progress row found — stale cookie
          deleteCookie(cookieName(formId));
          setSessionToken(null);
          setResponseId(null);
        }
      } catch (err) {
        logService.error('partialSave.restore', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
        deleteCookie(cookieName(formId));
        setSessionToken(null);
        setResponseId(null);
      }
    };

    restore();
  }, [formId, enabled]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Insert first partial response — generates id client-side
  // NOTE: Cannot use .select().single() — anon role has no SELECT policy
  // on form_responses, so PostgREST would roll back the INSERT.
  const insertPartial = useCallback(async (
    token: string,
    data: Record<string, any>,
    lastFieldId: string,
  ): Promise<string | null> => {
    const clientId = crypto.randomUUID();
    try {
      const { error } = await supabase
        .from('form_responses')
        .insert({
          id: clientId,
          form_id: formId,
          session_token: token,
          data,
          last_field_id: lastFieldId,
          status: 'in_progress',
        });

      if (error) {
        logService.error('partialSave.insert', error.message, { supabase_code: error.code, form_id: formId });
        return null;
      }

      return clientId;
    } catch (err) {
      logService.error('partialSave.insert', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return null;
    }
  }, [formId]);

  // Update existing partial response via RPC
  const updatePartial = useCallback(async (
    respId: string,
    token: string,
    data: Record<string, any>,
    lastFieldId: string,
    retry = true,
  ): Promise<boolean> => {
    try {
      const { data: success, error } = await supabase.rpc('update_partial_response', {
        p_response_id: respId,
        p_session_token: token,
        p_data: data,
        p_last_field_id: lastFieldId,
      });

      if (error) {
        logService.error('partialSave.update', error.message, { supabase_code: error.code, form_id: formId, response_id: respId });
        if (retry) {
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
          return updatePartial(respId, token, data, lastFieldId, false);
        }
        return false;
      }

      if (success === false) {
        // Session invalid — clean up
        logService.warn('partialSave.update', 'Session invalid, cleaning up', { form_id: formId, response_id: respId });
        deleteCookie(cookieName(formId));
        setSessionToken(null);
        setResponseId(null);
        return false;
      }

      return true;
    } catch (err) {
      logService.error('partialSave.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      if (retry) {
        await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
        return updatePartial(respId, token, data, lastFieldId, false);
      }
      return false;
    }
  }, [formId]);

  // Core save logic (called by debounce or flush)
  const executeSave = useCallback(async (data: Record<string, any>, lastFieldId: string) => {
    if (!hasInputData(data)) return;

    setIsSaving(true);
    try {
      let token = sessionToken;
      let respId = responseId;

      // First save — generate token + INSERT
      if (!token || !respId) {
        if (insertingRef.current) return; // prevent concurrent inserts
        insertingRef.current = true;

        token = crypto.randomUUID();
        setSessionToken(token);

        respId = await insertPartial(token, data, lastFieldId);
        insertingRef.current = false;

        if (!respId) return; // INSERT failed, stay in memory

        setResponseId(respId);
        setCookie(cookieName(formId), `${token}:${respId}`, cookieExpiryDays);
      } else {
        // Subsequent saves — UPDATE via RPC
        await updatePartial(respId, token, data, lastFieldId);
      }

      setLastSavedAt(new Date());
    } finally {
      setIsSaving(false);
    }
  }, [sessionToken, responseId, formId, cookieExpiryDays, insertPartial, updatePartial]);

  // Public: save with debounce
  const savePartial = useCallback(async (data: Record<string, any>, lastFieldId: string) => {
    if (!enabled) return;

    latestDataRef.current = data;
    latestFieldRef.current = lastFieldId;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (latestDataRef.current && latestFieldRef.current) {
        executeSave(latestDataRef.current, latestFieldRef.current);
      }
    }, DEBOUNCE_MS);
  }, [enabled, executeSave]);

  // Public: force immediate save (cancel debounce, save now)
  const flush = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (latestDataRef.current && latestFieldRef.current) {
      await executeSave(latestDataRef.current, latestFieldRef.current);
    }
  }, [executeSave]);

  // Public: final submit
  const submitFinal = useCallback(async (
    data: Record<string, any>,
    scores?: Record<string, any> | null,
  ): Promise<{ error: string | null }> => {
    // Cancel any pending debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // If we have an active partial session, complete it via RPC
    if (responseId && sessionToken) {
      try {
        const { data: success, error } = await supabase.rpc('complete_partial_response', {
          p_response_id: responseId,
          p_session_token: sessionToken,
          p_data: data,
          p_scores: scores ?? null,
        });

        if (error) {
          logService.error('partialSave.complete', error.message, { supabase_code: error.code, form_id: formId, response_id: responseId });
          return { error: 'Erro ao enviar resposta. Tente novamente.' };
        }

        if (success === false) {
          // Session invalid — fallback to fresh INSERT
          logService.warn('partialSave.complete', 'Session invalid, falling back to INSERT', { form_id: formId });
          return fallbackInsert(data, scores);
        }

        // Success — clean up cookie
        deleteCookie(cookieName(formId));
        setSessionToken(null);
        setResponseId(null);
        return { error: null };
      } catch (err) {
        logService.error('partialSave.complete', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
        return { error: 'Erro inesperado ao enviar' };
      }
    }

    // No partial session — do a regular INSERT
    return fallbackInsert(data, scores);
  }, [responseId, sessionToken, formId]);

  // Fallback: regular INSERT when no partial session exists
  const fallbackInsert = useCallback(async (
    data: Record<string, any>,
    scores?: Record<string, any> | null,
  ): Promise<{ error: string | null }> => {
    try {
      const insertPayload: Record<string, any> = { form_id: formId, data, status: 'completed' };
      if (scores !== undefined && scores !== null) insertPayload.scores = scores;

      const { error: insertError } = await supabase
        .from('form_responses')
        .insert(insertPayload);

      if (insertError) {
        logService.error('partialSave.fallbackInsert', insertError.message, { supabase_code: insertError.code, form_id: formId });
        return { error: 'Erro ao enviar resposta. Tente novamente.' };
      }

      deleteCookie(cookieName(formId));
      return { error: null };
    } catch (err) {
      logService.error('partialSave.fallbackInsert', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return { error: 'Erro inesperado ao enviar' };
    }
  }, [formId]);

  // Public: discard partial and start fresh
  const clearSession = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    deleteCookie(cookieName(formId));
    setSessionToken(null);
    setResponseId(null);
    setHasExistingSession(false);
    setExistingSessionData(null);
    setExistingSessionUpdatedAt(null);
    setLastSavedAt(null);
  }, [formId]);

  // Graceful degradation: if disabled, return noop
  if (!enabled) return NOOP_RETURN;

  return {
    sessionToken,
    isSaving,
    lastSavedAt,
    responseId,
    savePartial,
    flush,
    submitFinal,
    hasExistingSession,
    existingSessionData,
    existingSessionUpdatedAt,
    clearSession,
  };
}
